import React, { useState, useEffect } from 'react';
import '../styles/UpcomingEvents.css';

export function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const sessionToken = localStorage.getItem('session_token');

      const response = await fetch('/api/events/upcoming', {
        headers: {
          ...(sessionToken && { 'X-Session-Token': sessionToken }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();
      setEvents(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="card mb-4">
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">📅 Upcoming Events</h5>
        </div>
        <div className="card-body text-center">
          <div className="spinner-border text-info" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card mb-4">
        <div className="card-header bg-danger text-white">
          <h5 className="mb-0">📅 Upcoming Events</h5>
        </div>
        <div className="card-body">
          <div className="alert alert-danger mb-0">{error}</div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="card mb-4">
        <div className="card-header bg-info text-white">
          <h5 className="mb-0">📅 Upcoming Events</h5>
        </div>
        <div className="card-body">
          <p className="text-muted mb-0">No upcoming events at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-header bg-info text-white">
        <h5 className="mb-0">📅 Upcoming Events ({events.length})</h5>
      </div>
      <div className="card-body">
        <div className="events-list">
          {events.map((event) => (
            <div key={event.id} className="event-item">
              {event.image_url && (
                <div className="event-image mb-3" style={{ height: '200px', overflow: 'hidden', borderRadius: '8px' }}>
                  <img src={event.image_url} alt={event.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}
              <div className="event-header">
                <h6 className="event-title">{event.name}</h6>
                {!event.is_public && (
                  <span className="badge bg-warning">Private</span>
                )}
              </div>

              <p className="event-description">{event.description}</p>

              <div className="event-details">
                <div className="detail-row">
                  <span className="label">📍 Location:</span>
                  <span className="value">{event.location}</span>
                </div>
                <div className="detail-row">
                  <span className="label">📅 Date:</span>
                  <span className="value">{formatDate(event.date)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">🕐 Time:</span>
                  <span className="value">{formatTime(event.time)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">👥 Capacity:</span>
                  <span className="value">
                    {event.registrations}/{event.capacity} 
                    {event.registrations < event.capacity ? (
                      <span className="badge bg-success ms-2">{event.capacity - event.registrations} spots left</span>
                    ) : (
                      <span className="badge bg-danger ms-2">Full</span>
                    )}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">🎟️ Ticket Price:</span>
                  <span className="value fw-bold text-success">
                    {Number(event.price) > 0 ? `$${Number(event.price).toFixed(2)}` : 'Free'}
                  </span>
                </div>
              </div>

              <div className="event-actions">
                <a href={`/events/${event.id}`} className="btn btn-sm btn-primary">
                  View Details
                </a>
                <a href={`/events/${event.id}#register`} className="btn btn-sm btn-success">
                  Register Now
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
